import {createTheme} from '@mui/material/styles';

/**
 * Global MUI design system theme (application-wide).
 *
 * This file defines shared styling rules used across ALL MUI components:
 * - palette (colors)
 * - typography (fonts, headings)
 * - spacing & shape (radius)
 * - component-level defaults (via `components`)
 *
 * It is NOT used for page-specific or feature-specific styling.
 *
 * Local UI adjustments should be done via:
 * - `sx` prop (preferred for one-offs)
 * - `styled()` API (for reusable local components)
 *
 * NOTE: CssBaseline is intentionally NOT applied globally during the
 * coexistence period (while semantic-ui-css is still loaded).
 * Enable it inside MuiProvider only after semantic-ui-css is removed.
 */

const theme = createTheme({
    palette: {
        alert: {
            success: {
                main: '#a3c293',
                light: '#fcfff5',
                dark: '#1a531b',
            },
            info: {
                main: '#95d5ea',
                light: '#F8FFFF',
                dark: '#276F86',
            },
            warning: {
                main: '#bdab9d',
                light: '#FFFAF3',
                dark: '#573A08',
            },
            error: {
                main: '#e0b4b4',
                light: '#fff6f6',
                dark: '#9f3a38',
            },
            default: {
                main: '#cfd0d1',
                light: '#f8f8f9',
                dark: '#222426',
            },
        },
    },
    components: {
        MuiDivider: {
            styleOverrides: {
                root: {
                    margin: '1rem 0',
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                sizeSmall: {
                    borderRadius: 4,
                    fontSize: 10,
                    fontWeight: 700,
                    lineHeight: 1,
                    height: 'auto',
                    margin: '1px 2px',
                    padding: '2px 4px',
                    whiteSpace: 'normal',
                    verticalAlign: 'baseline',
                    '& .MuiChip-label': {
                        height: 'auto',
                        margin: '1px 2px',
                        padding: '2px 0',
                        whiteSpace: 'normal',
                        overflow: 'visible',
                        textOverflow: 'clip',
                    },
                },
            },
        },
        MuiCircularProgress: {
            defaultProps: {
                color: 'inherit',
            },
        },
        MuiBackdrop: {
            defaultProps: {
                transitionDuration: 0,
            },
            styleOverrides: {
                root: {
                    position: 'absolute',
                    zIndex: 1,
                    backgroundColor: 'rgba(0, 0, 0, 0.87)',
                    color: 'rgba(255, 255, 255, 0.85)',
                    '&[data-inverted="true"]': {
                        backgroundColor: 'rgba(255, 255, 255, 0.85)',
                        color: 'rgba(0, 0, 0, 0.87)',
                    },
                },
            },
        },
        MuiAlert: {
            styleOverrides: {
                root: ({theme}) => {
                    const alert = theme.palette.alert;
                    const severityColors = ({light, dark, main}) => ({
                        backgroundColor: light,
                        color: dark,
                        boxShadow: `0 0 0 1px ${main} inset, 0 0 0 0 transparent`,
                    });

                    return {
                        borderRadius: '4px',
                        marginBottom: '16px',
                        alignItems: 'center',
                        '&.MuiAlert-standard.MuiAlert-colorSuccess, &.MuiAlert-filled.MuiAlert-colorSuccess':
                            severityColors(alert.success),
                        '&.MuiAlert-standard.MuiAlert-colorError, &.MuiAlert-filled.MuiAlert-colorError':
                            severityColors(alert.error),
                        '&.MuiAlert-standard.MuiAlert-colorWarning, &.MuiAlert-filled.MuiAlert-colorWarning':
                            severityColors(alert.warning),
                        '&.MuiAlert-standard.MuiAlert-colorInfo, &.MuiAlert-filled.MuiAlert-colorInfo':
                            severityColors(alert.info),
                        '&.MuiAlert-standard.MuiAlert-colorDefault, &.MuiAlert-filled.MuiAlert-colorDefault':
                            severityColors(alert.default)
                        ,
                    };
                },
                message: {
                    padding: '5px',
                    fontSize: 14,
                    fontWeight: 500,
                    marginBottom: 0,
                },
            },
        },
    },
    typography: {
        fontFamily: [
            'Lato',
            '"Helvetica Neue"',
            'Arial',
            'Helvetica',
            'sans-serif',
        ].join(','),
        h6: {
            fontSize: 18,
            fontWeight: 700,
            lineHeight: 1.3,
        },
    },
});

export default theme;
